import React, { useEffect, useRef, useState } from "react";
import BimatterViewer from "bimatter-viewer";
import config from "../../config";
import {
    initViewer,
    setModel,
} from "../../app/features/viewer/viewerMainSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks/hooks";
import { keyEv } from "./ViewerKeyBoardEvents/ViewerKeyBoardEvents";
import ViewerButtons from "./ViewerButtons/ViewerButtons";
import {
    useComparePropertiesMutation,
    useGetGuidDictMutation,
    useGetStructureMutation,
} from "../../app/services/params/params";
import {
    selectViewerPropsVisibility,
    selectViewerTreeVisibility,
} from "../../app/features/viewer/buttonsSlice/buttonsSlice";
import ViewerProps from "./ViewerProps/ViewerProps";
import classes from "./Viewer.module.css";
import ViewerTree, { Chunk } from "./ViewerTree/ViewerTree";
import ViewerContextMenu from "./ViewerContextMenu/ViewerContextMenu";
import BimatterLoader from "../Loaders/BimatterLoader";
import ViewerProgressBar from "./ViewerProgressBar/ViewerProgressBar";
import {
    BufferAttribute,
    BufferGeometry,
    Color,
    DoubleSide,
    Group,
    Material,
    Matrix4,
    Mesh,
    MeshLambertMaterial,
    Vector3,
} from "three";
import { useNavigate } from "react-router-dom";

import { setAlert } from "../../app/features/alertSlice/alertSlice";
import { Model } from "bimatter-viewer/dist/Model/Model";
interface CompareVersionsViewerProps {
    model_paths: string[];
}
function CompareVersionsViewer({ model_paths }: CompareVersionsViewerProps) {
    const dispatch = useAppDispatch();
    const viewerTreeVisibility = useAppSelector(selectViewerTreeVisibility);
    const viewerPropsVisibility = useAppSelector(selectViewerPropsVisibility);
    const ref = useRef(null);
    const [curTree, setCurTree] = useState<{ [modelID: number]: Chunk } | null>(
        null
    );
    const [progress, setProgress] = useState<number | null>(null);

    const navigate = useNavigate();
    const [viewerOnFocuse, setViewerOnFocuse] = useState(true);
    const [loading, setLoading] = useState(true);
    const [getStructure] = useGetStructureMutation();
    const [getGuidDict] = useGetGuidDictMutation();
    const [compareProperties] = useComparePropertiesMutation();
    const mouseOn = () => {
        setViewerOnFocuse(true);
    };
    const mouseOut = () => {
        setViewerOnFocuse(false);
    };
    useEffect(() => {
        const viewer = new BimatterViewer({
            container: ref.current!,
            useDefaultTexture: false,
        });
        viewer.selector.selection.selectionCallback = () => {
            viewer.context.controls.setOrbitByClick();
        };
        viewer.context.renderer.threeRenderer.setClearColor(0x000000, 0);
        viewer.utils.propsUtils.useDefaultGetPropertiesById = false;

        viewer.utils.navigationCubeUtil.active = true;
        // if (!viewerTreeVisibility) return;
        // viewer.container.dispatchEvent(new CustomEvent("selectionTrigger"));
        // const model_path = "ar.min.bmt";

        const curKeyEv = (e: KeyboardEvent) => keyEv(e, viewer);
        loadModelWithWorker(viewer);
        document.addEventListener("keydown", curKeyEv);

        //@ts-ignore
        window.viewer = viewer;
        dispatch(
            initViewer(() => {
                return viewer;
            })
        );
        return () => {
            document.removeEventListener("keydown", curKeyEv);
            if (viewer) {
                viewer.container.removeEventListener("mouseenter", mouseOn);
                viewer.container.removeEventListener("mouseleave", mouseOut);
                viewer.container.remove();
                viewer.utils.navigationCubeUtil.cubeContainer?.remove();
                // viewer.dispose()
                console.log("need to dispose viewer");
            }
        };
    }, []);
    const loadModelWithWorker = async (viewer: BimatterViewer) => {
        if (!viewer) return;
        let modelsLoaded = 0;
        const resultEnum = {
            "new element": 0,
            "deleted element": 1,
            "geometry change": 2,
            "position change": 3,
        };
        const modelsCompareState: any = {
            position: {
                0: {},
                1: {},
            },
            result: {},
            guids: new Set<number>(),
            idsDict: {},
        };

        const materialSame = new MeshLambertMaterial({
            color: new Color("#eef1f4"),
            side: DoubleSide,
            transparent: true,
            opacity: 0.2,
        });
        const materialDeleted = new MeshLambertMaterial({
            color: new Color("red"),
            side: DoubleSide,
        });
        const materialAdded = new MeshLambertMaterial({
            color: new Color("green"),
            side: DoubleSide,
        });
        const materialMoved = new MeshLambertMaterial({
            color: new Color("pink"),
            side: DoubleSide,
        });
        const materialGeomChanged = new MeshLambertMaterial({
            color: new Color("blue"),
            side: DoubleSide,
        });
        const materialPropertiesChanged = new MeshLambertMaterial({
            color: new Color("Orange"),
            side: DoubleSide,
        });
        const newElementsArr: number[] = [];
        const removedElementsArr: number[] = [];
        const movedElementsArr: number[] = [];
        const geomChangedElementsArr: number[] = [];
        if (model_paths.length !== 2) {
            return dispatch(
                setAlert({
                    type: "error",
                    message: "Need to select 2 models to compare!",
                })
            );
        }
        const [firstModel, secondModel] = model_paths;
        const firstModelGuidDict = await getGuidDict(firstModel);
        const secondModelGuidDict = await getGuidDict(secondModel);
        const start = Date.now();
        await applyWorker(viewer, firstModel);
        applyWorker(viewer, secondModel, true).then(() => {
            compareProperties([firstModel, secondModel])
                .unwrap()
                .then((result) => {
                    const newElementsSet = new Set(newElementsArr);
                    const removedElementsSet = new Set(removedElementsArr);
                    const movedElementsSet = new Set(movedElementsArr);
                    const geomChangedElementsSet = new Set(
                        geomChangedElementsArr
                    );
                    const filteredRes = result.filter(
                        (id) =>
                            !newElementsSet.has(id) &&
                            !removedElementsSet.has(id) &&
                            !movedElementsSet.has(id) &&
                            !geomChangedElementsSet.has(id) &&
                            viewer.models[0].activeElements.has(id)
                    );
                    viewer.utils.geometryUtils.createGeometryChunk({
                        modelID: 0,
                        material: materialPropertiesChanged,
                        ids: filteredRes,
                    });
                    const last = viewer.models[0].threeGeometry.children.pop()!;
                    viewer.selector.selectorModels[0].push(last);
                    viewer.models[0].threeGeometry.children.push(last);
                });
            console.log(`Compare by ${Date.now() - start}ms`);
        });

        // for (const model_path of model_paths) {§
        //     // let splitedPath = model_path.split('/')
        //     // splitedPath.pop
        //     const await applyWorker(viewer, model_path).then(() => {
        //         if (modelsLoaded === model_paths.length) {
        //             try {
        //                 viewer.models[modelsLoaded - 1].fitToView();
        //             } catch {}
        //         }
        //     });
        //     i++;
        // }

        // if(file.file.name)
        function applyWorker(
            viewer: BimatterViewer,
            model_path: string,
            isSecond: boolean = false
        ) {
            return new Promise((resolve) => {
                const worker = new Worker(
                    new URL("./Workers/bmtLoaderWorker.js", import.meta.url),
                    {
                        type: "module",
                    }
                );
                const curModelId =
                    (Math.max(
                        ...Object.keys(viewer.models).map((n) => Number(n))
                    ) +
                        1) |
                    0;
                let model: Model | null = null;
                if (isSecond) {
                    model = viewer.addEmptyModel(curModelId);
                }
                const selectGroup = new Group();
                const preselectGroup = new Group();

                worker.onmessage = (data) => {
                    if (data.data.error) {
                        dispatch(
                            setAlert({
                                message:
                                    "Error 01 while loading model, please send this code to Developer",
                                type: "error",
                            })
                        );

                        navigate("/files");
                        return;
                    } else if (data.data.ready) {
                        console.log("ready");
                        if (model) {
                            viewer.selector.selectorModels[model.modelID] =
                                model.threeGeometry.children;
                            viewer.selector.selection._selectedMesh.add(
                                selectGroup
                            );

                            viewer.selector.preSelection._preSelectMesh.add(
                                preselectGroup
                            );
                            viewer.selector.selectedElements[model.modelID] =
                                new Set();
                            if (data.data.state && data.data.defaultState) {
                                model.setState(
                                    data.data.state,
                                    data.data.defaultState
                                );
                            }
                            modelsLoaded++;
                            dispatch(
                                setModel({
                                    modelID: model.modelID,
                                    model_id: model_path,
                                })
                            );
                            modelsCompareState.guids.forEach((guid: number) => {
                                const firstModelPosData =
                                    modelsCompareState.position[0][guid];
                                if (!firstModelPosData) {
                                    modelsCompareState.result[guid] =
                                        resultEnum["new element"];
                                } else {
                                    const normolizePosArr = (arr: number[]) => {
                                        const res = [];
                                        for (
                                            let index = 0;
                                            index < arr.length;
                                            index += 3
                                        ) {
                                            const x = arr[index];
                                            const y = arr[index + 1];
                                            const z = arr[index + 2];
                                            const vec = new Vector3(
                                                x,
                                                y,
                                                z
                                            ).normalize();
                                            res.push(vec.x, vec.y, vec.z);
                                        }
                                        return JSON.stringify(res.sort());
                                    };
                                    const secondModelPosData =
                                        modelsCompareState.position[1][guid];
                                    if (!secondModelPosData) {
                                        modelsCompareState.result[guid] =
                                            resultEnum["deleted element"];
                                    } else {
                                        if (
                                            normolizePosArr(
                                                firstModelPosData
                                            ) !==
                                            normolizePosArr(secondModelPosData)
                                        ) {
                                            modelsCompareState.result[guid] =
                                                resultEnum["geometry change"];
                                        } else if (
                                            JSON.stringify(
                                                firstModelPosData.sort()
                                            ) !==
                                            JSON.stringify(
                                                secondModelPosData.sort()
                                            )
                                        ) {
                                            modelsCompareState.result[guid] =
                                                resultEnum["position change"];
                                        }
                                    }
                                }
                            });

                            for (const guid of Object.keys(
                                modelsCompareState.result
                            )) {
                                const curId = Number(
                                    modelsCompareState.idsDict[guid]
                                );
                                switch (modelsCompareState.result[guid]) {
                                    case resultEnum["new element"]:
                                        newElementsArr.push(curId);
                                        break;
                                    case resultEnum["deleted element"]:
                                        removedElementsArr.push(curId);
                                        break;
                                    case resultEnum["geometry change"]:
                                        geomChangedElementsArr.push(curId);
                                        break;
                                    case resultEnum["position change"]:
                                        movedElementsArr.push(curId);
                                        break;
                                }
                            }
                            getStructure(model.modelID)
                                .unwrap()
                                .then((structure) => {
                                    if (!model) return;
                                    model.properties.setStructure(structure);
                                    setCurTree((ex) => {
                                        if (!model) return ex;
                                        if (!ex) {
                                            return {
                                                [model.modelID]: structure,
                                            };
                                        }
                                        return {
                                            ...ex,
                                            [model.modelID]: structure,
                                        };
                                    });
                                    worker.postMessage({
                                        type: "state",
                                        data: model.threeGeometry.children.map(
                                            (child) => ({
                                                index: (child as Mesh).geometry
                                                    .index?.array,
                                                ids: (child as Mesh).geometry
                                                    .attributes.ids.array,
                                                matInd: child.name,
                                            })
                                        ),
                                    });
                                });
                            viewer.models[0].fitToView();
                            // console.log(modelsCompareState);
                        } else {
                            resolve(true);
                            setProgress(null);
                            worker.terminate();
                        }
                    } else if (data.data.state) {
                        if (model) {
                            model.setState(
                                data.data.state,
                                data.data.defaultState,
                                data.data.activeElements
                            );

                            if (removedElementsArr.length) {
                                const newModel = viewer.addEmptyModel(
                                    curModelId + 1
                                );
                                const newSelectGroup = new Group();
                                const newPreselectGroup = new Group();
                                const newState: any = {
                                    idsMap: {},
                                    indMap: {},
                                    needsUpdate: new Set<number>(),
                                };
                                const newDefState: any = {
                                    idsMap: {},
                                    indMap: {},
                                };
                                const newActiveElements = new Set<number>();

                                const matId = Object.keys(
                                    newState.indMap
                                ).length;
                                const posData = modelsCompareState.position[0];
                                const posArr: number[] = [];
                                const idsArr: number[] = [];
                                const indArr: number[] = [];
                                let i = 0;
                                for (const guid of Object.keys(posData)) {
                                    const curPossArr = posData[guid];
                                    const id = modelsCompareState.idsDict[guid];

                                    if (!removedElementsArr.includes(id)) {
                                        continue;
                                    }
                                    const curIndArr: number[] = [];
                                    for (
                                        let index = 0;
                                        index < curPossArr.length;
                                        index += 3
                                    ) {
                                        const x = curPossArr[index];
                                        const y = curPossArr[index + 1];
                                        const z = curPossArr[index + 2];
                                        posArr.push(x, y, z);
                                        idsArr.push(id);
                                        curIndArr.push(i);
                                        indArr.push(i);
                                        i++;
                                    }
                                    newState.idsMap[id] = {
                                        [matId]: curIndArr,
                                    };
                                    newDefState.idsMap[id] = {
                                        [matId]: curIndArr,
                                    };
                                    newActiveElements.add(Number(id));
                                    // geom.setAttribute("position", posBufferAttr);
                                }
                                const typedIndArr = new Uint32Array(indArr);
                                newState.indMap[matId] = typedIndArr;
                                newDefState.indMap[matId] = typedIndArr;

                                const posBufferAttr = new BufferAttribute(
                                    new Float32Array(posArr),
                                    3
                                );
                                const idsBufferAttr = new BufferAttribute(
                                    new Uint32Array(idsArr),
                                    1
                                );

                                const geom = new BufferGeometry();
                                geom.setAttribute("position", posBufferAttr);
                                geom.setAttribute("ids", idsBufferAttr);
                                geom.setIndex(
                                    new BufferAttribute(typedIndArr, 1)
                                );
                                const mesh = new Mesh(geom, materialDeleted);
                                mesh.name = matId.toString();
                                geom.computeBoundingBox();

                                geom.computeBoundingSphere();
                                geom.computeVertexNormals();
                                // viewer.models[0].threeGeometry.add(mesh);
                                newModel.addMeshToModel(
                                    mesh,
                                    newSelectGroup,
                                    newPreselectGroup,
                                    false
                                );
                                newModel.setState(
                                    newState,
                                    newDefState,
                                    newActiveElements
                                );
                                viewer.selector.selectorModels[
                                    newModel.modelID
                                ] = newModel.threeGeometry.children;
                                viewer.selector.selection._selectedMesh.add(
                                    newSelectGroup
                                );
                                viewer.selector.preSelection._preSelectMesh.add(
                                    newPreselectGroup
                                );
                                viewer.selector.selectedElements[
                                    newModel.modelID
                                ] = new Set();
                                getStructure(firstModel)
                                    .unwrap()
                                    .then((structure) => {
                                        newModel.properties.setStructure(
                                            structure
                                        );
                                        setCurTree((ex) => {
                                            if (!ex) {
                                                return {
                                                    [newModel.modelID]:
                                                        structure,
                                                };
                                            }
                                            return {
                                                ...ex,
                                                [newModel.modelID]: structure,
                                            };
                                        });
                                        dispatch(
                                            setModel({
                                                modelID: newModel.modelID,
                                                model_id: firstModel,
                                            })
                                        );
                                    });
                            }
                            let offset = 0;
                            if (newElementsArr.length) {
                                viewer.utils.geometryUtils.createGeometryChunk({
                                    modelID: 0,
                                    ids: newElementsArr,
                                    material: materialAdded,
                                });
                                offset += 1;
                            }

                            if (movedElementsArr.length) {
                                viewer.utils.geometryUtils.createGeometryChunk({
                                    modelID: 0,
                                    ids: movedElementsArr,
                                    material: materialMoved,
                                });
                                offset += 1;
                            }
                            if (geomChangedElementsArr.length) {
                                viewer.utils.geometryUtils.createGeometryChunk({
                                    modelID: 0,
                                    ids: geomChangedElementsArr,
                                    material: materialGeomChanged,
                                });
                                offset += 1;
                            }

                            viewer.selector.selectorModels[0] =
                                viewer.selector.selectorModels[0].slice(
                                    -offset,
                                    viewer.selector.selectorModels[0].length
                                );
                            materialAdded.clippingPlanes =
                                viewer.utils.clippingUtils.planes;
                            materialMoved.clippingPlanes =
                                viewer.utils.clippingUtils.planes;
                            materialGeomChanged.clippingPlanes =
                                viewer.utils.clippingUtils.planes;

                            resolve(model);
                        }
                        resolve(true);
                        setProgress(null);
                        worker.terminate();
                    } else if (data.data.setMatrix) {
                        const matrixArr = JSON.parse(data.data.setMatrix);
                        const matrix = new Matrix4()
                            .fromArray(matrixArr)
                            .invert();
                        if (!viewer.loaders.coordinationMatrix) {
                            viewer.loaders.coordinationMatrix = matrix
                                .clone()
                                .invert();
                        }
                    } else if (data.data.process) {
                        if (loading) setLoading(false);
                        const process = data.data.process;
                        const persent = Math.round(
                            (process.current * 100) / process.total
                        );
                        setProgress(persent);
                        // progress.innerHTML = !process.total
                        //     ? process.type
                        //     : `${process.type} ${persent}%`;
                    } else {
                        const start = Date.now();
                        const geomData = data.data.geom;
                        const geom = new BufferGeometry();
                        const posBufferAttr = new BufferAttribute(
                            geomData.pos,
                            3
                        );
                        const idsBufferAttr = new BufferAttribute(
                            geomData.ids,
                            1
                        );
                        geom.setAttribute("position", posBufferAttr);
                        geom.setAttribute("ids", idsBufferAttr);
                        geom.setAttribute(
                            "normal",
                            new BufferAttribute(geomData.normal, 3)
                        );
                        const indexBufferAttr = new BufferAttribute(
                            new Uint32Array(geomData.ind),
                            1
                        );
                        geom.setIndex(indexBufferAttr);

                        const mesh = new Mesh(geom, materialSame);
                        for (const index of geomData.ind) {
                            const id = idsBufferAttr.getX(Number(index));
                            const x = posBufferAttr.getX(Number(index));
                            const y = posBufferAttr.getY(Number(index));
                            const z = posBufferAttr.getZ(Number(index));

                            const guid = model
                                ? //@ts-ignore
                                  secondModelGuidDict.data[id]
                                : //@ts-ignore
                                  firstModelGuidDict.data[id];
                            const elementData =
                                modelsCompareState.position[model ? 1 : 0][
                                    guid
                                ];
                            if (elementData) {
                                elementData.push(x, y, z);
                            } else {
                                modelsCompareState.position[model ? 1 : 0][
                                    guid
                                ] = [x, y, z];
                            }
                            modelsCompareState.guids.add(guid);
                            modelsCompareState.idsDict[guid] = id;
                        }

                        if (
                            Object.keys(viewer.models).length &&
                            viewer.loaders.coordinationMatrix
                        ) {
                            const matrixArr = JSON.parse(data.data.matrix);
                            const matrix = new Matrix4()
                                .fromArray(matrixArr)
                                .invert();
                            geom.applyMatrix4(matrix);
                            geom.applyMatrix4(
                                viewer.loaders.coordinationMatrix
                            );
                        }

                        // geom.computeVertexNormals();
                        geom.computeBoundingBox();

                        geom.computeBoundingSphere();

                        mesh.name = data.data.name.toString();
                        if (model) {
                            model.addMeshToModel(
                                mesh,
                                selectGroup,
                                preselectGroup,
                                model.threeGeometry.children.length === 1
                            );
                        }
                    }
                    // console.log(data);
                };
                // worker.postMessage("./Models/model.ifc");
                worker.postMessage({
                    type: "load",
                    data: `${config.SERVER}/${model_path}`,
                });
            });
        }
    };
    return (
        <ViewerContextMenu enable={viewerOnFocuse}>
            <BimatterLoader loading={loading}></BimatterLoader>
            {progress ? (
                <ViewerProgressBar progress={progress}></ViewerProgressBar>
            ) : (
                <></>
            )}
            <ViewerButtons></ViewerButtons>
            {viewerTreeVisibility ? (
                curTree ? (
                    <ViewerTree tree={structuredClone(curTree)} />
                ) : (
                    <></>
                )
            ) : (
                <></>
            )}

            <div className={classes.viewerBackground}></div>
            <div
                ref={ref}
                className={classes.viewerContainer}
                onMouseEnter={mouseOn}
                onMouseLeave={mouseOut}
            />

            {viewerPropsVisibility ? <ViewerProps></ViewerProps> : <></>}
        </ViewerContextMenu>
    );
}

export default CompareVersionsViewer;
